import { createContext, useContext } from 'react';
import type { ApolloError } from '@apollo/client';

/**
 * Shape of a single wallet payment method from the ECM member-details query,
 * limited to the fields used to select the member's Chase card.
 */
export type EcmPaymentMethod = {
  panLast4?: string;
  cobrand?: {
    issuer?: { code?: string };
    issuerProduct?: { code?: string };
  };
};

/**
 * Shape of the shared ECM member-details query response. Covers both the wallet
 * payment methods (used by CobrandWebSmallBanner) and the loyalty certificates
 * total (used by DataTiles) so a single query serves every ECM child.
 */
export type EcmMemberDetailsResponse = {
  customer?: {
    loyaltyInformation?: {
      certificates?: {
        total?: number;
      };
    };
    wallet?: {
      paymentMethods?: EcmPaymentMethod[];
    };
  };
};

/**
 * Value provided by the CobrandWrapper when it centralizes the ECM UXL call.
 * A `null` context (default) means no centralization is in effect, and children
 * should fall back to their own data resolution.
 */
export type CobrandEcmContextValue = {
  /** Whether the ECM variation (enableEcmHubDashboard) is active. */
  isEcmEnabled: boolean;
  ecmData?: EcmMemberDetailsResponse;
  ecmLoading: boolean;
  ecmError?: ApolloError;
} | null;

export const CobrandEcmContext = createContext<CobrandEcmContextValue>(null);

/**
 * Returns the shared ECM data when a CobrandWrapper is centralizing the ECM
 * call, or `null` when the component is used standalone.
 */
export const useCobrandEcm = (): CobrandEcmContextValue => useContext(CobrandEcmContext);
